import { Link } from "react-router-dom";
import { Camera, Receipt, BookOpen, Truck, ChevronRight, type LucideIcon } from "lucide-react";

// =============================================================================
// CaptureChooserScreen — /capture
// T-096 buffer screen between the mobile bottom-nav Camera button and the
// downstream capture surfaces. James asked for a clean intermediate so the
// crew see a clear menu of "what am I capturing right now" rather than
// being deep-linked into one pack automatically.
//
// Three actions:
//   * HOP Photos  → Acceptance Centre (Open tab)  — same destination as the
//                   "HOP library" link in pack detail screens
//   * Receipt     → /receipts/new
//   * Site Diary  → coming soon (T-011) — greyed
//
// Designed for mobile-first; renders fine on desktop too with wider cards.
// =============================================================================

export default function CaptureChooserScreen() {
  return (
    <div className="min-h-screen pb-20 px-4 pt-6 md:pt-10">
      <div className="max-w-md mx-auto">
        <div className="mb-1 text-[11px] font-mono uppercase tracking-wider2 text-text-muted">
          Capture
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">What are you capturing?</h1>
        <p className="text-text-muted text-[13px] mt-1.5">
          Pick the kind of capture and we'll take you straight to it.
        </p>

        <div className="mt-6 space-y-3">
          <CaptureCard
            to="/acceptance?tab=open"
            icon={Camera}
            title="HOP Photos"
            description="Open the Acceptance Centre to find your active HOP pack and start uploading photos."
          />
          <CaptureCard
            to="/receipts/new"
            icon={Receipt}
            title="Receipt"
            description="Snap a receipt for AP processing — Coles, fuel, materials, anything."
          />
          <CaptureCard
            to="/capture/diary"
            icon={BookOpen}
            title="Site Diary"
            description="Daily field log — who's on site, what got done, conditions, delays, outage info, end-of-day photo."
          />
          <CaptureCard
            to="/capture/delivery"
            icon={Truck}
            title="Delivery"
            description="Materials came in — snap the docket, Ops reads the project number and lines, you confirm the job and who signed."
          />
        </div>
      </div>
    </div>
  );
}

function CaptureCard({
  to, icon: Icon, title, description, disabled,
}: {
  to: string;
  icon: LucideIcon;   // was React.ComponentType<{size,className}> — lucide icons never fit it (4 tsc errors)
  title: string;
  description: string;
  disabled?: boolean;
}) {
  const inner = (
    <div className={`relative flex items-start gap-4 p-4 rounded-xl border ${
      disabled
        ? "bg-ink-raised/40 border-line/60 opacity-60"
        : "bg-ink-raised border-line hover:border-accent active:scale-[0.99] transition-all"
    }`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
        disabled ? "bg-ink text-text-dim" : "bg-accent/15 text-accent"
      }`}>
        <Icon size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className={`text-[15px] font-semibold ${disabled ? "text-text-muted" : "text-text"}`}>
            {title}
          </h3>
          {disabled && (
            <span className="text-[9px] font-mono uppercase tracking-wider2 text-text-dim bg-ink rounded px-1.5 py-0.5">
              soon
            </span>
          )}
        </div>
        <p className="text-[12px] text-text-muted mt-1 leading-snug">
          {description}
        </p>
      </div>
      {!disabled && (
        <ChevronRight size={18} className="text-text-dim self-center flex-shrink-0" />
      )}
    </div>
  );

  if (disabled) return inner;
  return <Link to={to} className="block">{inner}</Link>;
}
